import clsx from 'clsx';
import HubertLabel from 'components/HubertUI/HubertLabel';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import MatrixEvaluation from 'components/Templates/builder/Evaluation/EvaluationPopover/MatrixEvaluation';
import { useQuestionEvaluation } from 'hooks/useQuestionEvaluation';
import { useTemplateId } from 'hooks/useTemplate';
import { CirclePlus } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { addMatrixColumnByIndex, addMatrixRowByIndex, updateFormField } from 'rtk/Question.Editor.Slice';
import { selectQuestionLanguage, selectQuestionMode } from 'rtk/questionEditorSelector';
import { setCustomEvaluation } from 'rtk/TemplateSlice';
import { EMatrixEvaluationType } from 'types/evaluation.types';
import { EEventType } from 'types/events.enum';
import type { TFormFieldUpdater } from '../FormFieldUpdater';
import FormFieldUpdater from '../FormFieldUpdater';
import FormMatrixColumn from './FormMatrixColumn';
import FormMatrixRow from './FormMatrixRow';
import { type TFormMatrix, EQuestionType } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';
interface Props extends ComponentProps<'div'> {
  form: TFormMatrix;
  formIndex: number;
}

const CreateFormMatrix = ({ form, formIndex, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateId = useTemplateId();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const { evaluation, isLoading: isEvaluationLoading } = useQuestionEvaluation(questionId);

  const formFieldValues: TFormFieldUpdater[] = useMemo(
    () => [
      {
        label: t('form.matrix.helpText.label'),
        description: t('form.matrix.helpText.description'),
        formKey: 'help_text',
        fieldType: 'rich-text',
        formValuesKey: 'helpText',
        form,
        formIndex,
      },
    ],
    [t, form, formIndex],
  );

  return (
    <div {...props}>
      {formFieldValues.map((x, index) => {
        return (
          <FormFieldUpdater
            key={`form-field-updater-${form.id}-${index}`}
            label={x.label}
            description={x.description}
            formKey={x.formKey}
            fieldType={x.fieldType}
            formValuesKey={x.formValuesKey}
            form={x.form}
            formIndex={x.formIndex}
          />
        );
      })}

      <div className={clsx('flex flex-col justify-between')}>
        <div className="text-sm font-semibold">{t('form.matrix.rows')}</div>
        <div className={clsx('flex flex-col')}>
          {form.rows.length ? (
            form.rows.map((row, rowIndex) => {
              return (
                <FormMatrixRow
                  className="w-full"
                  key={`create-form-matrix-row-${row.id}`}
                  row={row}
                  formIndex={formIndex}
                  rowIndex={rowIndex}
                />
              );
            })
          ) : (
            <CirclePlus
              className="cursor-pointer"
              onClick={() => {
                dispatch(addMatrixRowByIndex({ questionId, formIndex, rowIndex: 0, questionLanguage }));
              }}
            />
          )}
        </div>
      </div>

      <div className={clsx('flex flex-col justify-between')}>
        <div className="text-sm font-semibold">{t('form.matrix.columns')}</div>
        <div className={clsx('flex flex-col')}>
          {form.columns.length ? (
            form.columns.map((column, columnIndex) => {
              return (
                <FormMatrixColumn
                  className="w-full"
                  key={`create-form-matrix-column-${column.id}`}
                  column={column}
                  formIndex={formIndex}
                  columnIndex={columnIndex}
                />
              );
            })
          ) : (
            <CirclePlus
              className="cursor-pointer"
              onClick={() => {
                dispatch(addMatrixColumnByIndex({ questionId, formIndex, columnIndex: 0, questionLanguage }));
              }}
            />
          )}
        </div>
      </div>

      {isEvaluationLoading ? null : (
        <div {...props} className={clsx('w-full', 'flex-row- flex justify-between', 'hubert-vertical-center', 'my-4')}>
          <div>
            <HubertLabel>{t('form.matrix.evaluation')}</HubertLabel>
          </div>

          <div>
            <MatrixEvaluation
              nodeId={''}
              allowedTabs={[EMatrixEvaluationType.Weighted, EMatrixEvaluationType.Conditional]}
              // @ts-ignore
              form={form}
              customKey={`${form.id}`}
              // @ts-ignore
              evaluation={evaluation?.evaluation[form.id]}
              isDefault={true}
              onClick={() => {}}
              onEvaluationChange={(e: any) => {
                dispatch(updateFormField({ questionId, formIndex, field: 'evaluation', value: e }));

                if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
                  dispatch(
                    setCustomEvaluation({
                      templateId,
                      eventType: EEventType.MODIFIED,
                      resource: {
                        nodeId: questionId,
                        nodeType: EQuestionType.Form,
                        formType: form.type,
                        key: form.id,
                        evaluation: e,
                      },
                      description: `User updated evaluation to ${e} for form ${form.id} in question ${questionId} in template ${templateId}.`,
                    }),
                  );
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateFormMatrix;
